import { useEffect, useState } from "react";
import { getMedia } from "@/lib/api";

/**
 * ProfilePhoto — renders the admin-uploaded profile image when available.
 *
 * If no image has been uploaded via /admin/media (key: "profile"), we render an
 * elegant monogram placeholder instead of a random stock image. This avoids the
 * "wrong person flashes on load, then swaps" issue the user reported.
 */
export function ProfilePhoto({ className = "", alt = "Aditya Baheti portrait", monogram = "AB" }) {
    const [state, setState] = useState({ url: null, loaded: false });

    useEffect(() => {
        let cancelled = false;
        getMedia("profile")
            .then((r) => {
                if (cancelled) return;
                const url = r?.url || null;
                if (!url) {
                    setState({ url: null, loaded: true });
                    return;
                }
                // Pre-load so we don't get a broken/pixellated flash before the image is ready.
                const img = new Image();
                img.onload = () => !cancelled && setState({ url, loaded: true });
                img.onerror = () => !cancelled && setState({ url: null, loaded: true });
                img.src = url;
            })
            .catch(() => !cancelled && setState({ url: null, loaded: true }));
        return () => { cancelled = true; };
    }, []);

    if (!state.url) {
        return <MonogramPlaceholder className={className} monogram={monogram} loading={!state.loaded} alt={alt} />;
    }

    return (
        <img
            src={state.url}
            alt={alt}
            decoding="async"
            loading="lazy"
            className={`${className} bg-secondary`}
            style={{ imageRendering: "auto" }}
            data-testid="site-profile-photo"
        />
    );
}

function MonogramPlaceholder({ className, monogram, loading, alt }) {
    return (
        <div
            role="img"
            aria-label={alt}
            className={`relative ${className} bg-gradient-to-br from-[hsl(var(--ink))] via-[hsl(var(--ocean))] to-[hsl(var(--oxblood))] overflow-hidden`}
            data-testid="site-profile-photo-placeholder"
        >
            {/* Soft radial highlight */}
            <div
                aria-hidden
                className="absolute inset-0"
                style={{
                    background:
                        "radial-gradient(120% 80% at 30% 20%, hsl(var(--paper) / 0.18), transparent 55%), radial-gradient(80% 60% at 80% 90%, hsl(var(--copper) / 0.35), transparent 60%)",
                }}
            />
            {/* Subtle grain */}
            <div
                aria-hidden
                className="absolute inset-0 opacity-[0.12] mix-blend-overlay"
                style={{
                    backgroundImage:
                        "url(\"data:image/svg+xml;utf8,<svg xmlns='http://www.w3.org/2000/svg' width='120' height='120'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/></filter><rect width='120' height='120' filter='url(%23n)' opacity='0.9'/></svg>\")",
                }}
            />
            {/* Monogram */}
            <div className="absolute inset-0 grid place-items-center">
                <div className="text-center px-6">
                    <div className="mx-auto h-12 w-12 rounded-full border border-white/25 grid place-items-center text-white/80 mb-3">
                        <svg width="18" height="18" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="1.5">
                            <circle cx="12" cy="8" r="4" />
                            <path d="M4 21c1.5-4 5-6 8-6s6.5 2 8 6" />
                        </svg>
                    </div>
                    <p className="font-display text-[clamp(3rem,10vw,6rem)] leading-none text-white tracking-tight">{monogram}</p>
                    <p className="mt-3 font-mono text-[10px] uppercase tracking-[0.28em] text-white/70">
                        {loading ? "Loading portrait…" : "Portrait forthcoming"}
                    </p>
                </div>
            </div>
        </div>
    );
}
